import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography} from '@mui/material';
import DashboardShell from '../components/DashboardShell';
import CpcLoader from '../components/CpcLoader';
import {toastSuccess} from '../../../utils/utils';
import {
    getChildrensAssessmentRequestService,
    getCpcGovIndicatorsService,
    isCpcGovLoggedIn,
    requestChildrensAssessmentService,
} from '../../../services/cpcGov.service';
import {toastWarning} from '../../../utils/utils';
import {DashboardProvider, useDashboard} from '../context/DashboardContext';
import {
    INDICATORS,
    PURPLE,
    STAKEHOLDERS,
    TARGETS,
    statusColors,
    statusLabel,
    stakeById,
    targetById,
} from '../data';

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69',
    navyDeep: '#003D69',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const STAKEHOLDER_CHIPS = [
    {id: 'all', label: 'All stakeholders'},
    ...STAKEHOLDERS.map((s) => ({id: String(s.id), label: `S${s.id} · ${s.name}`, dot: s.color})),
];
const TARGET_OPTIONS = [
    {v: 'all', l: 'All targets'},
    ...TARGETS.map((t) => ({v: String(t.n), l: `Target ${String(t.n).padStart(2, '0')} · ${t.name}`})),
];
// `v` stays the internal Yes/Partial/No status value rows are filtered
// against; `l` is the label shown to the reviewer, which must read Met /
// Partially / Not Met like everywhere else — never the raw internal value.
const STATUS_OPTIONS = [
    {v: 'all', l: 'All statuses'}, {v: 'Yes', l: 'Met'}, {v: 'Partial', l: 'Partially'}, {v: 'No', l: 'Not Met'},
];
/**
 * The three stages Seong Min verified with Dr. Park (5 Aug). These replace the
 * earlier "Revision proposed / Agreed" wording, which described the DECISION on
 * an item rather than the PROGRESS of an indicator.
 */
const REVIEW_OPTIONS = [
    {v: 'all', l: 'All reviews'},
    {v: 'not_reviewed', l: 'Not reviewed'},
    {v: 'in_progress', l: 'In progress'},
    {v: 'reviewed', l: 'Reviewed'},
];

/**
 * Review states.
 *
 * The three-stage set (not_reviewed / in_progress / reviewed) is the version
 * Seong Min verified with Dr. Park on 5 Aug and SUPERSEDES the earlier
 * agreed/proposed wording. The legacy keys are kept alongside because the
 * signed-out mock still produces them via indReviewState().
 */
const REVIEW_STYLE = {
    not_reviewed: {dot: '#c9c9c0', fg: '#a5a59c', label: 'Not reviewed'},
    in_progress: {dot: '#c99a3f', fg: '#8a6a1a', label: 'In progress'},
    reviewed: {dot: '#5c9469', fg: '#3f7350', label: 'Reviewed'},
    // Legacy keys from the offline mock.
    none: {dot: '#c9c9c0', fg: '#a5a59c', label: 'Not reviewed'},
    agreed: {dot: '#5c9469', fg: '#3f7350', label: 'Agreed'},
    proposed: {dot: '#c99a3f', fg: '#8a6a1a', label: 'Revision proposed'},
};

/**
 * The offline mock still reports the old vocabulary (none / agreed / proposed).
 * Translate it so the Review filter behaves the same signed in or out.
 * "proposed" maps to reviewed: under Dr. Park's rule an indicator is Reviewed
 * once every item is agreed OR its evidence is complete — a proposal with
 * evidence counts as done, not as outstanding.
 */
const MOCK_REVIEW_TO_STAGE = {
    none: 'not_reviewed',
    agreed: 'reviewed',
    proposed: 'reviewed',
};

/** "CPCI Target 01" -> "01", "CPCI Stakeholder 3" -> "3". */
const trailingNumber = (id) => {
    const m = String(id || '').match(/(\d+)\s*$/);
    return m ? m[1] : '';
};

/**
 * One API-02 row -> the shape this table already renders.
 *
 * The display CODE is `external_id` — Seong Min, 5 Aug: show the external id, and
 * if it ever reads too long they shorten it in `external_code_map`, which reaches
 * us with no redeploy. The mockup's "FC-05" form is not stored anywhere.
 */
const fromApiRow = (r) => ({
    code: r.external_id || r.indicator_id,
    name: r.name,
    t: trailingNumber(r.target_id),
    s: trailingNumber(r.stakeholder_id),
    // The table's helpers speak the mock's vocabulary; the API speaks the
    // CPC side's. Translate once, here, rather than in three render branches.
    status: r.status === 'Met' ? 'Yes' : r.status === 'Partially' ? 'Partial' : 'No',
    reviewState: (r.review && r.review.status) || 'not_reviewed',
    /**
     * The 15 Child indicators. Their rows are NOT clickable — Seong Min (Q3):
     * "the user cannot access SCR-DTL at all. This is because clicking is
     * disabled in SCR-EXP", and Dr. Park's slide: "Indicator 1-15 -> disabled,
     * and only able to press 'Request...' and pop-up if they do."
     *
     * The API decides this, so the rule stays with the data.
     */
    isChild: !!r.is_child_indicator,
});

const filterLabelSx = {fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, mb: 1};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        background: C.mist,
        fontSize: 14,
        fontFamily: 'inherit',
        color: C.navyDeep,
        '& fieldset': {borderColor: 'rgba(0,60,105,.2)'},
    },
};

const Dot = ({color, sx}) => <Box sx={{width: 8, height: 8, borderRadius: '50%', flex: 'none', background: color, ...sx}}/>;

/**
 * What the reviewer sees once a request exists.
 *
 * ONE state, because the reviewer is only ever told that DQ has their request.
 * The approve/decline decision is internal and the server does not send it — see
 * _forGovernment in assessment_request.cpcgov.service.js. Nothing follows from
 * that decision yet, and announcing one here would be telling a country an
 * outcome nobody has agreed to send.
 */
const REQUEST_STATE = {
    heading: 'Children’s Assessment requested',
    body: 'The CPC Index team has your request and will follow up.',
    label: 'Requested',
};

const ChecklistExplorerPage = () => {
    const history = useHistory();
    const location = useLocation();
    const {indReviewState} = useDashboard();
    const [notice, setNotice] = useState('');

    // Every filter (plus the category tab and search box) is seeded from the
    // URL on mount, and mirrored back into it below. This is deliberately
    // scoped to THIS history entry only (not sessionStorage/anything longer-
    // lived) — filters should survive opening an indicator's detail page and
    // clicking back to it (goBack() restores this exact URL), but a fresh
    // sidebar link to Explorer from an unrelated page (Overview, Review
    // Progress) should start clean rather than resurrect an old filter set.
    const initialParams = new URLSearchParams(location.search);
    const [stakeFilter, setStakeFilter] = useState(() => initialParams.get('stakeholder') || 'all');
    const [targetFilter, setTargetFilter] = useState(() => initialParams.get('target') || 'all');
    const [statusFilter, setStatusFilter] = useState(() => initialParams.get('status') || 'all');
    const [reviewFilter, setReviewFilter] = useState(() => initialParams.get('review') || 'all');
    const [search, setSearch] = useState(() => initialParams.get('q') || '');
    // Which category of indicator the table is showing — a tab, not a scroll,
    // since Government Checklist and Children's Assessment rows behave too
    // differently (clickable detail vs. request-only) to read as one list.
    const [categoryTab, setCategoryTab] = useState(() => initialParams.get('category') || 'checklist');

    // Keep the URL in step with the filters — a `replace` (not `push`) so
    // adjusting a filter never adds its own Back-button stop, but the current
    // history entry always carries enough for goBack() (from an indicator's
    // detail page) to restore this exact filtered view.
    useEffect(() => {
        const params = new URLSearchParams();
        if (stakeFilter !== 'all') params.set('stakeholder', stakeFilter);
        if (targetFilter !== 'all') params.set('target', targetFilter);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (reviewFilter !== 'all') params.set('review', reviewFilter);
        if (search.trim()) params.set('q', search.trim());
        if (categoryTab !== 'checklist') params.set('category', categoryTab);
        const qs = params.toString();
        const nextSearch = qs ? `?${qs}` : '';
        if (nextSearch !== location.search) {
            history.replace({pathname: location.pathname, search: nextSearch}, location.state);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stakeFilter, targetFilter, statusFilter, reviewFilter, search, categoryTab]);

    // Signed in -> the real checklist for this reviewer's own country (API-02).
    // Signed out -> the bundled sample, so the screen stays reviewable before
    // accounts are provisioned. `apiRows === null` means "not loaded / not live".
    const authed = isCpcGovLoggedIn();
    const [apiRows, setApiRows] = useState(null);
    const [loadError, setLoadError] = useState('');
    // Signed in, we render NOTHING until the real rows arrive. Falling back to the
    // bundled sample while the request is in flight made the table flash a handful
    // of mock indicators — some of them marked "Agreed" — before snapping to the
    // real 100. Briefly showing a reviewer review states that are not theirs is
    // worse than showing them nothing for 800ms.
    const [loading, setLoading] = useState(isCpcGovLoggedIn());

    /**
     * The Child indicator a reviewer has asked about, or null.
     *
     * Dr. Park's slide: those rows are disabled and "only able to press
     * 'Request...' and pop-up if they do". The pop-up explains why there is no
     * checklist detail; it does not submit anything yet — API-09 (Request
     * Children's Assessment) is not built, and what a request should DO has not
     * been specified.
     */
    const [childRequest, setChildRequest] = useState(null);

    /**
     * The country's Children's Assessment request (API-09).
     *
     * ONE per country, not per indicator — all 15 Child indicators come from the
     * same survey — so this is fetched once for the page rather than per row.
     */
    const [assessmentRequest, setAssessmentRequest] = useState(null);
    const [canRequest, setCanRequest] = useState(true);
    const [requestNote, setRequestNote] = useState('');
    const [sendingRequest, setSendingRequest] = useState(false);
    const [requestError, setRequestError] = useState('');

    useEffect(() => {
        if (!authed) return undefined;
        let alive = true;
        getChildrensAssessmentRequestService()
            .then((res) => {
                if (!alive || !res?.data) return;
                setAssessmentRequest(res.data.request || null);
                setCanRequest(res.data.can_request !== false);
            })
            .catch(() => { /* the button still works; the server is the guard */ });
        return () => { alive = false; };
    }, [authed]);

    const sendAssessmentRequest = () => {
        setSendingRequest(true);
        setRequestError('');
        requestChildrensAssessmentService(requestNote)
            .then((res) => {
                const saved = res?.data?.request || null;
                setAssessmentRequest(saved);
                setCanRequest(false);
                setRequestNote('');
                setChildRequest(null);
                toastSuccess(res?.data?.already_requested
                    ? 'Your country has already requested the Children’s Assessment.'
                    : 'Request sent to the CPC Index team.');
            })
            .catch((err) => {
                // Kept open with the note intact so the reviewer can retry.
                setRequestError(err?.message || 'Could not send your request. Please try again.');
            })
            .finally(() => setSendingRequest(false));
    };

    useEffect(() => {
        document.title = 'Checklist Explorer';
    }, []);

    useEffect(() => {
        if (!authed) return undefined;

        let alive = true;
        // Fetched unfiltered and filtered in the browser: it is 100 rows for one
        // country, so a round trip per keystroke would cost more than it saves.
        getCpcGovIndicatorsService({})
            .then((res) => {
                if (!alive) return;
                const list = res?.data?.indicators;
                if (Array.isArray(list)) setApiRows(list.map(fromApiRow));
                setLoading(false);
            })
            .catch((err) => {
                if (!alive) return;
                setLoadError(err?.message || 'Could not load the checklist.');
                // Failed: fall back to the sample rather than an empty screen.
                setLoading(false);
            });
        return () => { alive = false; };
    }, [authed]);

    // Parameterized on a hypothetical stakeholder so a chip click can check
    // "would this filter combination show anything?" before committing to it,
    // without duplicating the target/status/review/search filtering logic.
    const rowsForStake = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (apiRows) {
            return (stake) => apiRows.filter((r) => {
                if (stake !== 'all' && String(r.s) !== String(stake)) return false;
                if (targetFilter !== 'all' && String(+r.t) !== String(+targetFilter)) return false;
                if (statusFilter !== 'all' && r.status !== statusFilter) return false;
                if (reviewFilter !== 'all' && r.reviewState !== reviewFilter) return false;
                if (q && !`${r.code} ${r.name}`.toLowerCase().includes(q)) return false;
                return true;
            });
        }

        return (stake) => INDICATORS.filter((ind) => {
            if (stake !== 'all' && ind.s !== +stake) return false;
            if (targetFilter !== 'all' && ind.t !== +targetFilter) return false;
            if (statusFilter !== 'all' && ind.status !== statusFilter) return false;
            const stage = MOCK_REVIEW_TO_STAGE[indReviewState(ind)] || 'not_reviewed';
            if (reviewFilter !== 'all' && stage !== reviewFilter) return false;
            if (q) {
                const hay = `${ind.code} ${ind.name} ${ind.question} ${stakeById(ind.s)?.name} ${targetById(ind.t)?.name}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [apiRows, targetFilter, statusFilter, reviewFilter, search, indReviewState]);

    const rows = useMemo(() => rowsForStake(stakeFilter), [rowsForStake, stakeFilter]);

    const checklistRows = useMemo(() => rows.filter((r) => !r.isChild), [rows]);
    const assessmentRows = useMemo(() => rows.filter((r) => r.isChild), [rows]);
    const activeRows = categoryTab === 'checklist' ? checklistRows : assessmentRows;

    /**
     * How many rows this stakeholder would have in each category tab, given
     * whatever target/status/review/search filters are already set. Checked
     * before committing to a chip click so the table never has to fall back
     * on "No indicators in this category match these filters" — the chip
     * either lands on whichever tab actually has data, or refuses to apply.
     */
    const stakeholderTabCounts = (stakeId) => {
        const candidateRows = rowsForStake(stakeId);
        return {
            checklist: candidateRows.filter((r) => !r.isChild).length,
            assessment: candidateRows.filter((r) => r.isChild).length,
        };
    };

    const selectStakeFilter = (c) => {
        if (c.id === 'all') {
            setNotice('');
            setStakeFilter('all');
            return;
        }

        const counts = stakeholderTabCounts(c.id);
        if (counts[categoryTab] > 0) {
            setNotice('');
            setStakeFilter(c.id);
            return;
        }

        const otherTab = categoryTab === 'checklist' ? 'assessment' : 'checklist';
        if (counts[otherTab] > 0) {
            setNotice('');
            setStakeFilter(c.id);
            setCategoryTab(otherTab);
            return;
        }

        // Inline, not a toast — toasts are reserved elsewhere in the app for
        // server response outcomes, and this is neither a server call nor an
        // error, just "there's nothing to show for this combination".
        setNotice(`${c.label} has no indicators in Government Checklist or Children’s Assessment matching the current filters — showing all stakeholders instead.`);
        setStakeFilter('all');
    };

    return (
        <DashboardShell active="explorer">
            <Box
                sx={{
                    padding: '40px 56px 80px',
                    '@media (max-width: 1439px)': {paddingLeft: '40px', paddingRight: '28px'},
                    '@media (max-width: 720px)': {padding: '24px 20px 56px'},
                }}
            >
                {/*
                  * Only shown when the visitor actually clicked a card to get
                  * here — `location.state` is set on that specific push and
                  * is gone on a reload, a bookmarked link, or the plain
                  * "Open Checklist Explorer" button, so this never appears
                  * for someone who didn't come from a card in the first
                  * place.
                  */}
                {location.state?.fromOverview && (
                    <Tooltip title="Return to the National Overview" arrow>
                        <Button
                            onClick={() => history.goBack()}
                            sx={{border: 'none', background: 'transparent', color: C.cyanDeep, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none', padding: 0, minWidth: 'auto', mb: 1.75, '&:hover': {background: 'transparent'}}}
                        >
                            ← Back
                        </Button>
                    </Tooltip>
                )}
                <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.25}}>
                    Checklist Explorer
                </Typography>
                <Typography component="h1" sx={{margin: '0 0 14px', fontSize: 28, fontWeight: 700, color: C.text}}>
                    National Child Online Protection System Checklist Indicators
                </Typography>
                <Typography sx={{fontSize: 19, lineHeight: 1.55, color: C.muted, margin: '0 0 28px'}}>
                    Review the 85 Government Capability Indicators in one place. Confirm the current assessment,
                    propose evidence-based revisions where needed, and track review completion. The validated
                    results provide a clear foundation for the national child online protection action plan.
                </Typography>

                <Box sx={{background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', padding: '20px 24px', mb: 2.5}}>
                    <Typography sx={filterLabelSx}>Stakeholder</Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.25}}>
                        {STAKEHOLDER_CHIPS.map((c) => {
                            const active = stakeFilter === c.id;
                            return (
                                <Button
                                    key={c.id}
                                    onClick={() => selectStakeFilter(c)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        padding: '8px 13px',
                                        border: '1px solid',
                                        borderColor: active ? PURPLE : 'rgba(0,0,0,.14)',
                                        background: active ? PURPLE : C.surface,
                                        color: active ? C.white : C.text,
                                        fontFamily: 'inherit',
                                        textTransform: 'none',
                                        '&:hover': {background: active ? PURPLE : C.surface},
                                    }}
                                >
                                    {c.dot && <Dot color={c.dot}/>}
                                    {c.label}
                                </Button>
                            );
                        })}
                    </Box>

                    {notice && (
                        <Box sx={{background: '#fdf6f2', border: '1px solid rgba(192,85,58,.28)', color: '#a3452d', padding: '10px 14px', fontSize: 13.5, fontWeight: 600, mb: 2.25}}>
                            {notice}
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr) 1.4fr'},
                            gap: 1.75,
                        }}
                    >
                        <Box>
                            <Typography sx={filterLabelSx}>Target</Typography>
                            <TextField select fullWidth size="small" variant="outlined" value={targetFilter} onChange={(e) => setTargetFilter(e.target.value)} sx={fieldSx}>
                                {TARGET_OPTIONS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box>
                            <Typography sx={filterLabelSx}>Status</Typography>
                            <TextField select fullWidth size="small" variant="outlined" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={fieldSx}>
                                {STATUS_OPTIONS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box>
                            <Typography sx={filterLabelSx}>Review</Typography>
                            <TextField select fullWidth size="small" variant="outlined" value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} sx={fieldSx}>
                                {REVIEW_OPTIONS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box>
                            <Typography sx={filterLabelSx}>Search</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search indicators, codes, keywords…"
                                sx={fieldSx}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.1))', flexWrap: 'wrap', gap: 1.25}}>
                        <Tabs
                            value={categoryTab}
                            onChange={(_, v) => setCategoryTab(v)}
                            TabIndicatorProps={{style: {backgroundColor: C.cyan, height: 3}}}
                            sx={{
                                minHeight: 'auto',
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontFamily: 'inherit',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: C.muted,
                                    minWidth: 'auto',
                                    minHeight: 'auto',
                                    padding: '16px 4px',
                                    marginRight: 3,
                                    '&:hover': {color: C.text},
                                },
                                '& .Mui-selected': {color: `${C.text} !important`},
                            }}
                        >
                            <Tab value="checklist" label={`Government Checklist (${checklistRows.length})`}/>
                            <Tab value="assessment" label={`Children’s Assessment (${assessmentRows.length})`}/>
                        </Tabs>
                        <Tooltip title="See how much of the checklist has been reviewed" arrow>
                            <Button
                                onClick={() => history.push('/dq_pro/cpc-government-dashboard/progress')}
                                sx={{border: 'none', background: 'transparent', color: C.cyanDeep, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none', padding: 0, minWidth: 'auto', '&:hover': {background: 'transparent', textDecoration: 'underline'}}}
                            >
                                Review Progress →
                            </Button>
                        </Tooltip>
                    </Box>
                    {loadError && (
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: '#b3261e', padding: '10px 20px 0'}}>
                            {loadError} Showing sample data.
                        </Typography>
                    )}

                    {/*
                      * ONE control for the whole tab, not one per row.
                      *
                      * A request is per COUNTRY — all 15 Child indicators come from
                      * the same survey, so every row would fire the identical call
                      * and produce the identical single request. Fifteen buttons
                      * that do one thing read as fifteen separate things, and after
                      * clicking any one of them the other fourteen change state on
                      * their own, which is worse. The rows carry status only.
                      */}
                    {categoryTab === 'assessment' && !loading && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            margin: '0 20px 4px',
                            padding: '14px 16px',
                            background: C.mist,
                            border: '1px solid rgba(0,60,105,.12)',
                        }}>
                            <Box>
                                <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.navy, mb: 0.25}}>
                                    {canRequest
                                        ? 'These indicators are measured directly with children'
                                        : REQUEST_STATE.heading}
                                </Typography>
                                <Typography sx={{fontSize: 13, lineHeight: 1.55, color: C.muted}}>
                                    {canRequest
                                        ? 'They come from one survey, so they cannot be reviewed item by item. One request covers all 15.'
                                        : `${REQUEST_STATE.body}${assessmentRequest?.createdAt
                                            ? ` Requested on ${new Date(assessmentRequest.createdAt).toLocaleDateString()}.`
                                            : ''}`}
                                </Typography>
                            </Box>

                            {canRequest ? (
                                <Button
                                    type="button"
                                    onClick={() => setChildRequest(assessmentRows[0] || {})}
                                    sx={{
                                        fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                        textTransform: 'none', whiteSpace: 'nowrap', borderRadius: 0,
                                        padding: '8px 18px', border: `1px solid ${C.cyanDeep}`,
                                        color: '#fff', background: C.cyanDeep,
                                        '&:hover': {background: C.navy, borderColor: C.navy},
                                    }}
                                >
                                    Request Children’s Assessment
                                </Button>
                            ) : (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75,
                                    fontSize: 13, fontWeight: 700, color: C.navy, whiteSpace: 'nowrap',
                                }}>
                                    <Dot color={C.cyanDeep}/> Requested
                                </Box>
                            )}
                        </Box>
                    )}

                    {loading ? (
                        <CpcLoader label="Loading your country’s checklist…"/>
                    ) : (
                        <Box sx={{overflowX: 'auto'}}>
                            <Table sx={{width: '100%', minWidth: 860, '& .MuiTableCell-root': {borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.06))'}}}>
                                <TableHead>
                                    <TableRow>
                                        {['Code', 'Indicator', 'Target #', 'Stakeholder #', 'Status (Met / Partially / Not Met)', 'Review'].map((h) => (
                                            <TableCell
                                                key={h}
                                                sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, padding: '10px 20px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.1)) !important'}}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{padding: '48px 20px', textAlign: 'center', fontSize: 14, color: C.muted, border: 'none'}}>
                                                No indicators in this category match these filters.
                                            </TableCell>
                                        </TableRow>
                                    ) : activeRows.map((r) => {
                                        const sc = statusColors(r.status);
                                        // API rows carry their own rolled-up review state;
                                        // mock rows derive it from local decisions.
                                        const rs = REVIEW_STYLE[r.reviewState || indReviewState(r)]
                                            || REVIEW_STYLE.not_reviewed;
                                        const stake = stakeById(+r.s);
                                        return (
                                            <TableRow
                                                key={r.code}
                                                onClick={r.isChild
                                                    ? undefined
                                                    : () => history.push(`/dq_pro/cpc-government-dashboard/explorer/${r.code}`)}
                                                sx={r.isChild
                                                    // Child indicators are scored from the Children's
                                                    // Assessment, so there is no checklist detail to open.
                                                    // No pointer, no hover — the row must not look clickable.
                                                    ? {cursor: 'default'}
                                                    : {cursor: 'pointer', transition: 'background .1s ease', '&:hover': {background: C.mist}}}
                                            >
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: 'nowrap'}}>{r.code}</TableCell>
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, color: C.text}}>{r.name}</TableCell>
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, color: C.text}}>{r.t}</TableCell>
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, color: C.text}}>
                                                    <Dot color={stake?.color} sx={{display: 'inline-block', mr: 0.75}}/>
                                                    {r.s}
                                                </TableCell>
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, color: C.text}}>
                                                    <Box component="span" sx={{display: 'inline-block', fontSize: 12, fontWeight: 700, padding: '4px 10px', background: sc.bg, color: sc.fg}}>
                                                        {statusLabel(r.status)}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{padding: '13px 20px', fontSize: 13.5, color: C.text}}>
                                                    {r.isChild ? (
                                                        // Status only. A Child indicator has no checklist items
                                                        // to review, and the one action available belongs to the
                                                        // whole tab rather than to any single row — see the
                                                        // banner above the table.
                                                        <Tooltip
                                                            title={canRequest
                                                                ? 'Measured with children. Use the button above to ask the CPC Index team — one request covers all 15.'
                                                                : `${REQUEST_STATE.body} One request covers all 15 Child indicators.`}
                                                            arrow
                                                        >
                                                            <Box sx={{
                                                                display: 'flex', alignItems: 'center', gap: 0.75,
                                                                whiteSpace: 'nowrap', color: C.muted,
                                                            }}>
                                                                <Dot color={canRequest ? C.muted : C.cyanDeep}/>
                                                                {canRequest
                                                                    ? 'Not requested'
                                                                    : REQUEST_STATE.label}
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap', color: rs.fg}}>
                                                            <Dot color={rs.dot}/>
                                                            {rs.label}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Box>
            </Box>

            {/*
              * Child indicator pop-up. Explains why the row has no checklist
              * detail. It deliberately does NOT submit anything — API-09 is not
              * built, and what a request should actually do has not been
              * specified, so promising "request sent" would be a lie.
              */}
            <Dialog
                open={!!childRequest}
                onClose={() => setChildRequest(null)}
                maxWidth="sm"
                fullWidth
                // MUI Dialogs render through a portal straight onto document.body,
                // outside DashboardShell's own .cpc-gov-scope wrapper — so that
                // class has to be reapplied here too, or the dark-mode Typography
                // override (which only excludes .cpc-gov-scope's DESCENDANTS)
                // would clobber this dialog's own text colors.
                PaperProps={{className: 'cpc-gov-scope', sx: {borderRadius: 0}}}
            >
                <DialogTitle sx={{fontFamily: 'inherit', fontSize: 19, fontWeight: 700, color: C.text}}>
                    Children&rsquo;s Assessment
                </DialogTitle>
                <DialogContent>
                    {/*
                      * Country-level wording, deliberately. The dialog used to name
                      * the row that was clicked, which implied the request applied to
                      * that one indicator; it never did.
                      */}
                    <Typography sx={{fontSize: 14.5, lineHeight: 1.6, color: C.text, mb: 1.5}}>
                        <Box component="strong">Indicators 001&ndash;015</Box> are scored from the
                        Children&rsquo;s Assessment rather than the Government Checklist, so they have
                        no assessment items for your government to review here.
                    </Typography>
                    <Typography sx={{fontSize: 14.5, lineHeight: 1.6, color: C.muted, mb: 2}}>
                        To obtain or update these results, request the Children&rsquo;s Assessment for your
                        country through the CPC Index team. All 15 come from the same survey, so one
                        request covers them together.
                    </Typography>

                    {!canRequest && assessmentRequest ? (
                        <Box sx={{background: C.mist, border: '1px solid rgba(0,60,105,.12)', padding: '14px 16px'}}>
                            <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.navy, mb: 0.5}}>
                                {REQUEST_STATE.heading}
                            </Typography>
                            <Typography sx={{fontSize: 13.5, lineHeight: 1.55, color: C.muted}}>
                                {REQUEST_STATE.body}
                                {assessmentRequest.createdAt
                                    ? ` Requested on ${new Date(assessmentRequest.createdAt).toLocaleDateString()}.`
                                    : ''}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography component="span" sx={{fontSize: 13, fontWeight: 700, color: C.navy, display: 'block', mb: 0.75}}>
                                Anything you would like to add? (optional)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                variant="outlined"
                                placeholder="e.g. we would like this refreshed for the 2026 cycle"
                                value={requestNote}
                                onChange={(e) => setRequestNote(e.target.value)}
                                sx={fieldSx}
                            />
                            {requestError ? (
                                <Typography sx={{fontSize: 12.5, color: '#b3261e', fontWeight: 600, mt: 1}}>
                                    {requestError}
                                </Typography>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{padding: '12px 24px 20px'}}>
                    {canRequest && (
                        <Tooltip title="Ask the CPC Index team to run the Children’s Assessment for your country" arrow>
                            <Box component="span">
                                <Button
                                    onClick={sendAssessmentRequest}
                                    disabled={sendingRequest}
                                    sx={{
                                        fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none',
                                        borderRadius: 0, padding: '8px 20px', mr: 1,
                                        border: `2px solid ${C.cyanDeep}`, background: C.cyanDeep, color: '#fff',
                                        '&:hover': {background: C.cyanDeep, borderColor: C.cyanDeep},
                                        '&.Mui-disabled': {color: '#fff', background: '#9fc4d8', borderColor: '#9fc4d8'},
                                    }}
                                >
                                    {sendingRequest ? 'Sending…' : 'Send request'}
                                </Button>
                            </Box>
                        </Tooltip>
                    )}
                    <Tooltip title="Close this notice" arrow>
                        <Button
                            onClick={() => setChildRequest(null)}
                            sx={{
                                fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none',
                                borderRadius: 0, padding: '8px 20px',
                                border: `2px solid ${C.navy}`, background: C.navy, color: '#fff',
                                '&:hover': {background: C.navyDark || C.navy, borderColor: C.navyDark || C.navy},
                            }}
                        >
                            Close
                        </Button>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </DashboardShell>
    );
};

const ChecklistExplorerPageWithProvider = () => (
    <DashboardProvider>
        <ChecklistExplorerPage/>
    </DashboardProvider>
);

export default ChecklistExplorerPageWithProvider;
